import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';
import styles from './DoctorPerformanceDashboard.module.css';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AdvancedDoctorFilters from './AdvancedDoctorFilters'; // Import the new component

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const DoctorPerformanceDashboard = () => {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtering states
    const [hospitalFilter, setHospitalFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');

    // Sorting states
    const [sortConfig, setSortConfig] = useState({ key: 'totalTestsReferred', direction: 'descending' });

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);

    // Tab state
    const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' or 'advanced'

    useEffect(() => {
        const fetchPerformanceData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('Authentication token not found.');
                    setLoading(false);
                    return;
                }
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/doctors/performance-report`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setDoctors(response.data);
            } catch (err) {
                setError('Failed to fetch doctor performance data.');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPerformanceData();
    }, []);

    const topPerformingDoctorsChartData = useMemo(() => {
        const topDoctors = [...doctors]
            .sort((a, b) => b.totalTestsReferred - a.totalTestsReferred)
            .slice(0, 5);

        return {
            labels: topDoctors.map(d => d.doctor_name),
            datasets: [{
                label: 'Total Tests Referred',
                data: topDoctors.map(d => d.totalTestsReferred),
                backgroundColor: 'rgba(52, 152, 219, 0.6)',
                borderColor: 'rgba(52, 152, 219, 1)',
                borderWidth: 1,
            }]
        };
    }, [doctors]);

    const mostConsistentDoctorsChartData = useMemo(() => {
        const consistentDoctors = [...doctors]
            // Ensure we only consider doctors with a positive, calculated average
            .filter(d => d.averageDaysBetweenReferrals && d.averageDaysBetweenReferrals > 0)
            .sort((a, b) => a.averageDaysBetweenReferrals - b.averageDaysBetweenReferrals)
            .slice(0, 5);

        return {
            labels: consistentDoctors.map(d => d.doctor_name),
            datasets: [{
                label: 'Avg. Days Between Referrals',
                data: consistentDoctors.map(d => d.averageDaysBetweenReferrals),
                backgroundColor: 'rgba(26, 188, 156, 0.6)',
                borderColor: 'rgba(26, 188, 156, 1)',
                borderWidth: 1,
            }]
        };
    }, [doctors]);

    const chartOptions = {
        scales: {
            y: {
                ticks: {
                    callback: function(value) {
                        return value.toFixed(2);
                    }
                }
            }
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += context.parsed.y.toFixed(2);
                        }
                        return label;
                    }
                }
            },
            datalabels: {
                anchor: 'end',
                align: 'top',
                formatter: (value) => {
                    return value.toFixed(2);
                },
                font: {
                    weight: 'bold'
                }
            }
        }
    };

    const sortedAndFilteredDoctors = useMemo(() => {
        let filteredDoctors = [...doctors];

        if (hospitalFilter) {
            filteredDoctors = filteredDoctors.filter(doc =>
                doc.hospital.name.toLowerCase().includes(hospitalFilter.toLowerCase())
            );
        }

        if (cityFilter) {
            filteredDoctors = filteredDoctors.filter(doc =>
                doc.hospital.city.toLowerCase().includes(cityFilter.toLowerCase())
            );
        }

        if (sortConfig.key) {
            filteredDoctors.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredDoctors;
    }, [doctors, hospitalFilter, cityFilter, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        } else if (sortConfig.key !== key) {
            // Default sort directions
            direction = (key === 'totalTestsReferred') ? 'descending' : 'ascending';
        }
        setSortConfig({ key, direction });
    };

    const handleExportDoctors = () => {
        if (!sortedAndFilteredDoctors.length) {
            alert('No data available to export.');
            return;
        }
        const data = sortedAndFilteredDoctors.map((doc, i) => ({
            'Sl. No.': i + 1,
            'Doctor Name': doc.doctor_name || '',
            'Hospital': doc.hospital?.name || '',
            'City': doc.hospital?.city || '',
            'Total Tests Referred': doc.totalTestsReferred ?? 0,
            'Last Referral Date': doc.lastReferralDate ? new Date(doc.lastReferralDate).toLocaleDateString() : '',
            'Avg. Days Between Referrals': typeof doc.averageDaysBetweenReferrals === 'number'
                ? doc.averageDaysBetweenReferrals.toFixed(1)
                : 'N/A',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const keys = Object.keys(data[0]);
        ws['!cols'] = keys.map(k => ({
            wch: Math.min(Math.max(k.length, ...data.map(r => String(r[k] ?? '').length)) + 2, 36)
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Doctor Performance');
        XLSX.writeFile(wb, 'Doctor_Performance.xlsx');
    };

    const handleViewDetails = (doctor) => {
        setSelectedDoctor(doctor);
        setModalOpen(true);
    };

    if (loading) return <div className={styles.loading}>Loading performance data...</div>;
    if (error) return <div className={styles.error}>{error}</div>;

    return (
        <div className={styles.dashboardContainer}>
            <h1 className={styles.headerTitle}>Doctor Performance Dashboard</h1>
            
            <div className={styles.tabRow}>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'dashboard' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                >
                    Dashboard
                </button>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'advanced' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('advanced')}
                >
                    Advanced Filters
                </button>
            </div>

            {activeTab === 'dashboard' && (
                <>
                    <div className={styles.chartsContainer}>
                        <div className={styles.chartWrapper}>
                            <h3 className={styles.chartTitle}>Top 5 Performing Doctors</h3>
                            {topPerformingDoctorsChartData.labels.length > 0 ? (
                                <Bar data={topPerformingDoctorsChartData} />
                            ) : (
                                <p className={styles.noDataMessage}>No data available to display chart.</p>
                            )}
                        </div>
                        <div className={styles.chartWrapper}>
                            <h3 className={styles.chartTitle}>Top 5 Most Consistent Doctors</h3>
                            {mostConsistentDoctorsChartData.labels.length > 0 ? (
                                <Bar data={mostConsistentDoctorsChartData} options={chartOptions} />
                            ) : (
                                <p className={styles.noDataMessage}>Not enough referral history to calculate consistency.</p>
                            )}
                        </div>
                    </div>

                    <div className={styles.filters}>
                        <input
                            type="text"
                            placeholder="Filter by Hospital Name..."
                            className={styles.filterInput}
                            value={hospitalFilter}
                            onChange={(e) => setHospitalFilter(e.target.value)}
                        />
                        <input
                            type="text"
                            placeholder="Filter by City..."
                            className={styles.filterInput}
                            value={cityFilter}
                            onChange={(e) => setCityFilter(e.target.value)}
                        />
                        <button
                            className={styles.exportIconBtn}
                            title="Export to Excel"
                            aria-label="Export to Excel"
                            onClick={handleExportDoctors}
                        >
                            <FaFileExcel size={20} />
                        </button>
                    </div>

                    <div className={styles.tableWrapper}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Doctor Name</th>
                                    <th>Hospital</th>
                                    <th>City</th>
                                    <th onClick={() => requestSort('totalTestsReferred')} className={styles.sortable}>Total Tests Referred</th>
                                    <th onClick={() => requestSort('lastReferralDate')} className={styles.sortable}>Last Referral Date</th>
                                    <th onClick={() => requestSort('averageDaysBetweenReferrals')} className={styles.sortable}>Avg. Days Between Referrals</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAndFilteredDoctors.map(doc => (
                                    <tr key={doc.doctor_id}>
                                        <td>{doc.doctor_name}</td>
                                        <td>{doc.hospital.name}</td>
                                        <td>{doc.hospital.city}</td>
                                        <td>{doc.totalTestsReferred}</td>
                                        <td>{new Date(doc.lastReferralDate).toLocaleDateString()}</td>
                                        <td>
                                            {typeof doc.averageDaysBetweenReferrals === 'number'
                                                ? doc.averageDaysBetweenReferrals.toFixed(1)
                                                : 'N/A'}
                                        </td>
                                        <td>
                                            <button onClick={() => handleViewDetails(doc)} className={styles.detailsBtn}>View Details</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {activeTab === 'advanced' && <AdvancedDoctorFilters doctors={doctors} />}

            {modalOpen && selectedDoctor && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.modalContent}>
                        <h2>Referral History for {selectedDoctor.doctor_name}</h2>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Patient Name</th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Tests Referred</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedDoctor.referralHistory.map(ref => (
                                    <tr key={ref.id}>
                                        <td>{ref.PatientName}</td>
                                        <td>{new Date(ref.createdAt).toLocaleString()}</td>
                                        <td>{ref.Amount}</td>
                                        <td>{ref.tests.map(t => t.test).join(', ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button onClick={() => setModalOpen(false)} className={styles.closeModalBtn}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorPerformanceDashboard;
